"use client"

import type { MenuItem } from "@/core/types/menuApi"
import Link from "next/link"
import Image from "next/image"
import { useState, useRef, useEffect } from "react"
import { ChevronLeft } from "lucide-react"
import { useDictionary } from "@/core/hooks/use-dictionary"

interface HeaderNavMenuProps {
  mainMenu: MenuItem[]
}

export default function HeaderNavMenu({ mainMenu }: HeaderNavMenuProps) {
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null)
  const [hoveredChild, setHoveredChild] = useState<MenuItem | null>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { dictionary } = useDictionary()

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setActiveItem(null)
      setHoveredChild(null)
    }, 300)
  }

  const handleMouseEnter = (item: MenuItem) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    if (item.children && item.children.length > 0) {
      setActiveItem(item)
    }
  }

  // تابع بستن منو
  const closeMenu = () => {
    setActiveItem(null)
    setHoveredChild(null)
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
  }

  return (
    <nav className="hidden lg:flex items-center gap-10 text-sm font-medium">
      {mainMenu.map((item) => {
        const hasChildren = item.children && item.children.length > 0

        return (
          <div
            key={item.id}
            className="relative"
            onMouseEnter={() => handleMouseEnter(item)}
            onMouseLeave={handleMouseLeave}
          >
            {/* منوی اصلی بدون children */}
            {!hasChildren ? (
              <Link
                href={item.link}
                onClick={closeMenu}
                className="text-primary-900 text-sm hover:text-primary transition-colors py-2 block relative group"
              >
                {item.title}
                <span className="absolute inset-x-0 bottom-0 h-px bg-primary origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rtl:origin-right ltr:origin-left" />
              </Link>
            ) : (
              <div className="text-primary-900 text-sm hover:text-primary transition-colors py-2 block relative group">
                {item.title}
                <span className="absolute inset-x-0 bottom-0 h-px bg-primary origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rtl:origin-right ltr:origin-left" />
              </div>
            )}

            {/* مگامنو */}
            {hasChildren && activeItem?.id === item.id && (
              <div
                className="fixed left-0 right-0 top-[72px] z-50 animate-in fade-in slide-in-from-top-2 duration-300"
                onMouseEnter={() => closeTimeoutRef.current && clearTimeout(closeTimeoutRef.current)}
                onMouseLeave={handleMouseLeave}
              >
                <div className="w-full bg-white shadow-2xl border-t">
                  <div className="mx-auto py-4 px-12 max-w-7xl">
                    <div className="grid grid-cols-12 min-h-90">
                      {/* ستون سمت چپ - لیست دسته‌ها */}
                      <div className="col-span-5 p-8">
                        <div className="space-y-2">
                          <div className="mb-8 -mt-2">
                            <Link
                              href={item.link}
                              onClick={closeMenu}
                              className="font-primary-bold text-sm flex items-center group"
                            >
                              {dictionary.ui.pages.seeAll} {item.title}
                              <ChevronLeft className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </Link>
                          </div>
                          {item.children!.map((child) => (
                            <Link
                              key={child.id}
                              href={child.link}
                              onClick={closeMenu}
                              onMouseEnter={() => setHoveredChild(child)}
                              className="relative block py-3 text-primary-600 text-sm hover:text-primary transition-colors duration-200 text-left font-medium group"
                            >
                              <span className="flex items-center justify-between gap-3">
                                {child.title}
                                <ChevronLeft className="w-5 h-5 stroke-1 ltr:rotate-180 text-primary opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out" />
                              </span>
                              <span className="absolute inset-x-0 bottom-0 h-px bg-primary origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rtl:origin-right ltr:origin-left" />
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* ستون سمت راست - تصویر */}
                      <div className="col-span-7 p-7 flex items-center justify-center relative overflow-hidden">
                        {hoveredChild?.imageUrl ? (
                          <div className="relative w-full aspect-video">
                            <Image
                              src={hoveredChild.imageUrl}
                              alt={hoveredChild.title}
                              fill
                              className="object-cover transition-transform duration-700 hover:scale-105"
                              priority
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )
}